import { datadogRum } from "@datadog/browser-rum";
import { REPORT_TYPE_KEYS, downloadFileBlob, downloadReportPdfFile } from "@utils/utils";
import { useState } from "react";

import {
  ApiReportTemplateExportFrameworkChoices,
  ExportTypeEnum,
  ReportTypeEnums,
  useGetExportReportDocumentQuery,
} from "@api/graphql/types-and-hooks";
import { useInFlightOnce } from "@hooks/useInFlightOnce";
import { ReportNode } from "@providers/ReportProvider";

interface UseReportExportProps {
  report: ReportNode;
  clientName: string;
  exportType?: ExportTypeEnum;
  onExportComplete?: () => void;
}

interface UseReportExportReturn {
  handleExport: () => Promise<void>;
  isExporting: boolean;
  exportError: string | null;
}

export const useReportExport = ({
  report,
  clientName,
  exportType = ExportTypeEnum.Docx,
  onExportComplete,
}: UseReportExportProps): UseReportExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const { refetch: getExportReport } = useGetExportReportDocumentQuery(
    {
      reportId: report?.id ?? "",
      exportType,
    },
    {
      enabled: false,
      retry: false,
    },
  );

  const runExport = async () => {
    if (!report) {
      throw new Error("Report is not available");
    }

    setIsExporting(true);
    setExportError(null);

    try {
      // use S3 download if the report was generated by TS worker; see useSignatureHandler.ts.
      // The template's `unifiedExport` field routes docxtemplater orgs through the same S3 download path.
      const exportFramework = report.reportTemplate?.exportFramework;
      const isUnifiedExport = report.reportTemplate?.unifiedExport ?? false;
      const useS3Download =
        exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars ||
        (isUnifiedExport && exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater);

      if (useS3Download) {
        // download files generated by TS worker (handlebars) and uploaded to s3
        const s3Key =
          exportType === ExportTypeEnum.Pdf
            ? report.reportPdfFileSignedUrl
            : exportType === ExportTypeEnum.Docx
              ? report.reportDocxFileSignedUrl
              : null;

        if (!s3Key) {
          throw new Error("Report file is not available");
        }

        const reportTypeKey = REPORT_TYPE_KEYS[report.reportType as unknown as ReportTypeEnums] || "Report";
        await downloadReportPdfFile(s3Key, reportTypeKey, clientName);
      } else {
        // get google docs file generated by google docs export framework
        const queryResult = await getExportReport();
        const file = queryResult.data?.exportReportDocument?.file;
        const fileName = queryResult.data?.exportReportDocument?.fileName;

        downloadFileBlob(file, fileName);
      }
    } catch (error) {
      datadogRum.addError(error, { type: "reportExportError" });
      console.error("Error during report export:", error);
      setExportError(error instanceof Error ? error.message : "Failed to export report");
    } finally {
      setIsExporting(false);
      onExportComplete?.();
    }
  };

  // PROA-2622: no workflow starts here -- this only downloads an already
  // generated file -- but `isExporting` gates the button a render too late, so
  // a double click yielded two downloads and two `onExportComplete`s.
  const handleExport = useInFlightOnce(runExport);

  return {
    handleExport,
    isExporting,
    exportError,
  };
};
